/**
 * Bundle 72 - The United Dosts Seating & Table Arrangement.
 *
 * Top-level admin seating page, mounted at
 *   /admin/events/:id/seating
 *
 * Three columns:
 *   Left  - Unseated attendee pool (draggable cards).
 *   Center - Canvas of tables (drop targets). Each table is a card
 *            with a header + seat slots. Drop an attendee card onto
 *            a seat -> commit. Attendees can also be dragged BETWEEN
 *            seats to move / swap.
 *   Right - Dost AI chat panel. Structured proposals render with an
 *           Apply / Reject pair of buttons.
 *
 * No em/en dashes anywhere. No eslint-disable. No photo/image alt copy.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Sparkles, RefreshCw, Download, Lock, Unlock,
  Trash2, Send, Bot, MessageSquare, Users, Copy,
  Plus, GripVertical, AlertTriangle, Keyboard,
  Wand2, HelpCircle, Mail,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import SendNarrativesModal from './components/SendNarrativesModal';

const _traitSignals = (att) => {
  const bits = [];
  const p = (att.personality || {}).type;
  if (p === 'e') bits.push('extrovert');
  else if (p === 'i') bits.push('introvert');
  for (const t of (att.traits || []).slice(0, 3)) {
    if (t && !bits.includes(t)) bits.push(t);
  }
  if (att.city && bits.length < 3) bits.push(`from ${att.city}`);
  return bits.slice(0, 3);
};

const AttendeeCard = ({ att, onDragStart, badge, isLocked, onToggleLock, onUnseat, rationale }) => {
  const signals = _traitSignals(att);
  const tipMsg = rationale
    ? `Placed here because ${rationale.replace(/^Placed here because\s*/i, '')}`
    : 'Not yet seated';
  return (
    <TooltipProvider delayDuration={500}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/registration_id', att.registration_id);
              e.dataTransfer.effectAllowed = 'move';
              if (onDragStart) onDragStart(att);
            }}
            className="group relative flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-2 py-1.5 cursor-grab active:cursor-grabbing hover:border-secondary-300 hover:shadow-sm transition-all duration-150"
            data-testid={`attendee-card-${att.registration_id}`}
          >
            <GripVertical className="h-3 w-3 text-neutral-300 shrink-0" />
            <div className="h-6 w-6 rounded-full bg-secondary-100 text-secondary-800 text-[10px] font-semibold flex items-center justify-center shrink-0">
              {(att.name || 'G').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold text-primary-900 truncate">
                {att.name}
              </div>
              <div className="text-[9px] text-neutral-500 truncate">
                {att.age ? `${att.age}` : ''}
                {att.gender ? ` ${att.gender[0].toUpperCase()}` : ''}
                {att.city ? ` | ${att.city}` : ''}
              </div>
            </div>
            {badge ? (
              <Badge
                variant="outline"
                className="h-4 text-[9px] px-1 bg-secondary-50 text-secondary-800 border-secondary-200"
              >
                {badge}
              </Badge>
            ) : null}
            {onToggleLock ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleLock(att); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-neutral-100"
                data-testid={`attendee-card-lock-${att.registration_id}`}
                title={isLocked ? 'Unlock seat' : 'Lock seat'}
              >
                {isLocked ? (
                  <Lock className="h-3 w-3 text-amber-600" />
                ) : (
                  <Unlock className="h-3 w-3 text-neutral-400" />
                )}
              </button>
            ) : null}
            {onUnseat ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onUnseat(att); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50"
                data-testid={`attendee-card-unseat-${att.registration_id}`}
                title="Unseat"
              >
                <Trash2 className="h-3 w-3 text-red-500" />
              </button>
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          data-testid={`attendee-tooltip-${att.registration_id}`}
          className="max-w-xs"
        >
          <div className="text-[11px] font-semibold mb-1">{att.name}</div>
          <div className="text-[10px] leading-snug">{tipMsg}</div>
          {signals.length ? (
            <div className="mt-1.5 text-[9px] text-primary-100 opacity-80">
              {signals.join(' | ')}
            </div>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const TableCard = ({ table, members, onDropRegId, onEditTable, onDeleteTable, onToggleLock, onUnseat, warnings }) => {
  const [dragOver, setDragOver] = useState(false);
  const cap = Number(table.capacity || 0);
  const over = members.length > cap;
  const genderMales = members.filter((m) => (m.gender || '').toLowerCase() === 'male').length;
  const genderFemales = members.filter((m) => (m.gender || '').toLowerCase() === 'female').length;
  return (
    <Card
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const rid = e.dataTransfer.getData('text/registration_id');
        if (rid) onDropRegId(rid, table.id);
      }}
      className={`shrink-0 w-64 border transition-all ${
        dragOver ? 'ring-2 ring-secondary-400 border-secondary-400' : 'border-neutral-200'
      } ${over ? 'bg-red-50/30' : 'bg-white'}`}
      data-testid={`seating-table-${table.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-primary-900 truncate" data-testid={`seating-table-name-${table.id}`}>
              {table.name}
            </div>
            <div className="text-[10px] text-neutral-500">
              {members.length}/{cap} seated {table.shape === 'rect' ? '(rect)' : '(round)'}
              {genderMales + genderFemales > 0
                ? ` | ${genderMales}M ${genderFemales}F`
                : ''}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onEditTable(table)}
              className="p-1 rounded hover:bg-neutral-100"
              data-testid={`seating-table-edit-${table.id}`}
              title="Edit table"
            >
              <Plus className="h-3 w-3 text-neutral-500 rotate-45" />
            </button>
            <button
              type="button"
              onClick={() => onDeleteTable(table)}
              className="p-1 rounded hover:bg-red-50"
              data-testid={`seating-table-delete-${table.id}`}
              title="Delete table"
            >
              <Trash2 className="h-3 w-3 text-red-500" />
            </button>
          </div>
        </div>
        {over ? (
          <div className="mb-2 text-[10px] text-red-700 inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Over capacity
          </div>
        ) : null}
        {warnings && warnings.length > 0 ? (
          <div className="mb-2 text-[10px] text-amber-800 space-y-0.5">
            {warnings.map((w, i) => (
              <div key={i} className="inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {w}
              </div>
            ))}
          </div>
        ) : null}
        <div className="space-y-1 transition-all duration-150">
          {members.length === 0 ? (
            <div className="text-[10px] italic text-neutral-400 text-center py-3">
              Drop attendees here
            </div>
          ) : members.map((m) => (
            <div
              key={m.registration_id}
              className="transition-all duration-150 ease-out"
              data-seat-index={m._seat_index}
              data-testid={`seat-slot-${table.id}-${m._seat_index}`}
            >
              <AttendeeCard
                att={m}
                badge={m.rationale ? 'AI' : null}
                isLocked={m._locked}
                onToggleLock={onToggleLock}
                onUnseat={onUnseat}
                rationale={m.rationale}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const AiChatMessage = ({ msg, onApplyProposal }) => {
  const proposals = msg.proposal || [];
  return (
    <div className={`mb-3 ${msg.role === 'user' ? 'text-right' : ''}`}>
      <div className={`inline-block max-w-[92%] rounded-lg px-3 py-2 text-xs ${
        msg.role === 'user'
          ? 'bg-primary-700 text-white'
          : 'bg-neutral-100 text-primary-900'
      }`} data-testid={`chat-msg-${msg.role}`}>
        <div className="whitespace-pre-wrap">{msg.content}</div>
      </div>
      {proposals.length > 0 ? (
        <div className="mt-2 border border-neutral-200 rounded-md p-2 bg-white text-left" data-testid="chat-proposal-card">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Proposed changes ({proposals.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {proposals.map((p, i) => (
              <div key={i} className="text-[11px] text-primary-900 flex items-start gap-1.5">
                <span className="shrink-0 text-secondary-700">{'->'}</span>
                <div>
                  <div className="font-semibold">
                    Move to {p.to_table ? `table ${(p.to_table || '').slice(0, 8)}` : 'unseat'}
                  </div>
                  <div className="text-[10px] text-neutral-500">
                    {p.reason || 'No reason provided'}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-1.5 justify-end">
            <Button
              size="sm" variant="outline" className="h-6 text-[10px]"
              onClick={() => onApplyProposal([])}
              data-testid="chat-proposal-reject"
            >
              Reject
            </Button>
            <Button
              size="sm" className="h-6 text-[10px]"
              onClick={() => onApplyProposal(proposals)}
              data-testid="chat-proposal-apply"
            >
              Apply
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const AdminSeatingPage = () => {
  const { id: eventId } = useParams();
  const [snapshot, setSnapshot] = useState({
    tables: [], assignments: [], attendees: [], unseated: [],
    constraints: [], counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [objective, setObjective] = useState('balance');
  const [autoBusy, setAutoBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);

  // Bundle 73 Task 1 - "Explain this arrangement" narrative state
  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [showCheatsheet, setShowCheatsheet] = useState(false);
  // Bundle 75 Task 1 - send-narratives modal + sent-log state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sentLog, setSentLog] = useState(null);
  const [sentLogOpen, setSentLogOpen] = useState(false);
  const [sentLogBusy, setSentLogBusy] = useState(false);
  // Bundle 73 Task 2b - canvas zoom + pan + undo/redo history
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const historyRef = useRef({ past: [], future: [] });
  const canvasRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/events/${eventId}/seating`);
      setSnapshot(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [eventId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Bundle 73 Task 1 - rehydrate cached narrative on mount so the
  // panel does not flash blank + re-hit the LLM every visit.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/events/${eventId}/seating/explain-arrangement`,
        );
        if (alive && data && data.tables && data.tables.length > 0) {
          setNarrative(data);
        }
      } catch (e) {
        // Silent - the "Explain" button will still work on demand.
      }
    })();
    return () => { alive = false; };
  }, [eventId]);

  const explainArrangement = async () => {
    setNarrativeBusy(true);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/seating/explain-arrangement`,
      );
      setNarrative(data);
      toast.success('Narrative refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Explain failed');
    } finally { setNarrativeBusy(false); }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success('Copied');
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  // Bundle 75 Task 1 - load the narrative sent-log tab.
  const fetchSentLog = useCallback(async () => {
    setSentLogBusy(true);
    try {
      const { data } = await api.get(
        `/admin/events/${eventId}/seating/narrative-sends`,
      );
      setSentLog(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sent log failed');
    } finally { setSentLogBusy(false); }
  }, [eventId]);
  const openSentLog = () => {
    setSentLogOpen(true);
    if (!sentLog) fetchSentLog();
  };
  const copyAllNarratives = () => {
    if (!narrative?.tables) return;
    const lines = [];
    const tbl_by_id = Object.fromEntries(
      (snapshot.tables || []).map((t) => [t.id, t.name]),
    );
    for (const row of narrative.tables) {
      const label = row.nickname
        ? `${tbl_by_id[row.table_id] || 'Table'} - ${row.nickname}`
        : tbl_by_id[row.table_id] || 'Table';
      lines.push(label);
      lines.push(row.narrative || '');
      lines.push('');
    }
    copyText(lines.join('\n').trimEnd());
  };

  // Bundle 73 Task 2b - undo / redo history
  const captureSnapshotForHistory = useCallback(() => {
    historyRef.current.past.push({
      assignments: JSON.parse(JSON.stringify(snapshot.assignments || [])),
    });
    if (historyRef.current.past.length > 20) {
      historyRef.current.past.shift();
    }
    historyRef.current.future = [];
  }, [snapshot.assignments]);

  const restoreAssignments = async (assignments) => {
    try {
      // Bulk write everything at once via unseat-then-reassign pattern.
      const payload = { assignments: assignments.map((a) => ({
        registration_id: a.registration_id,
        table_id: a.table_id,
        seat_index: a.seat_index,
        locked: !!a.locked,
      }))};
      await api.put(
        `/admin/events/${eventId}/seating/assignments/bulk`,
        payload,
      );
      await fetchAll();
    } catch (e) {
      toast.error('Restore failed');
    }
  };
  const undo = async () => {
    const prev = historyRef.current.past.pop();
    if (!prev) { toast.info('Nothing to undo'); return; }
    historyRef.current.future.push({
      assignments: JSON.parse(JSON.stringify(snapshot.assignments || [])),
    });
    await restoreAssignments(prev.assignments);
  };
  const redo = async () => {
    const nxt = historyRef.current.future.pop();
    if (!nxt) { toast.info('Nothing to redo'); return; }
    historyRef.current.past.push({
      assignments: JSON.parse(JSON.stringify(snapshot.assignments || [])),
    });
    await restoreAssignments(nxt.assignments);
  };

  const membersByTable = useMemo(() => {
    const m = {};
    for (const a of snapshot.assignments || []) {
      const att = (snapshot.attendees || []).find((x) => x.registration_id === a.registration_id);
      if (!att) continue;
      m[a.table_id] = m[a.table_id] || [];
      m[a.table_id].push({ ...att, rationale: a.rationale, _locked: !!a.locked, _seat_index: a.seat_index });
    }
    for (const k of Object.keys(m)) {
      m[k].sort((x, y) => (x._seat_index ?? 0) - (y._seat_index ?? 0));
    }
    return m;
  }, [snapshot]);

  const assignOne = async (rid, tableId) => {
    captureSnapshotForHistory();
    try {
      await api.put(`/admin/events/${eventId}/seating/assignments/bulk`, {
        assignments: [{ registration_id: rid, table_id: tableId, locked: false }],
      });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    }
  };

  const unseatOne = async (att) => {
    captureSnapshotForHistory();
    try {
      await api.put(`/admin/events/${eventId}/seating/assignments/bulk`, {
        assignments: [{ registration_id: att.registration_id, table_id: null }],
      });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unseat failed');
    }
  };

  const toggleLock = async (att) => {
    const cur = (snapshot.assignments || []).find((a) => a.registration_id === att.registration_id);
    if (!cur) return;
    try {
      await api.put(`/admin/events/${eventId}/seating/assignments/bulk`, {
        assignments: [{
          registration_id: att.registration_id,
          table_id: cur.table_id,
          seat_index: cur.seat_index,
          locked: !cur.locked,
        }],
      });
      await fetchAll();
    } catch (e) {
      toast.error('Toggle lock failed');
    }
  };

  const addTable = async () => {
    try {
      const name = window.prompt('Table name?', `Table ${snapshot.tables.length + 1}`) || `Table ${snapshot.tables.length + 1}`;
      const cap = parseInt(window.prompt('Capacity?', '8') || '8', 10);
      await api.post(`/admin/events/${eventId}/seating/tables`, {
        name, capacity: cap, shape: 'round',
        pos_x: 100 + snapshot.tables.length * 260,
        pos_y: 100,
      });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add table failed');
    }
  };
  const editTable = async (t) => {
    const name = window.prompt('Rename table', t.name) || t.name;
    const cap = parseInt(window.prompt('Capacity', String(t.capacity)) || String(t.capacity), 10);
    try {
      await api.put(`/admin/events/${eventId}/seating/tables/${t.id}`, {
        name, capacity: cap,
      });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };
  const deleteTable = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Unseat everyone first.`)) return;
    try {
      await api.delete(`/admin/events/${eventId}/seating/tables/${t.id}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const runAutoArrange = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/seating/auto-arrange`, {
        objective, respect_locked: true, max_iterations: 500,
      });
      setProposal(data);
      toast.success(`Proposed with score ${data.score}, ${data.diff?.length || 0} to move`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-arrange failed');
    } finally { setAutoBusy(false); }
  };
  const applyProposal = async () => {
    if (!proposal?.proposal_id) return;
    try {
      await api.post(`/admin/events/${eventId}/seating/auto-arrange/apply`, {
        proposal_id: proposal.proposal_id,
      });
      setProposal(null);
      await fetchAll();
      toast.success('Applied.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${api.defaults.baseURL}/admin/events/${eventId}/seating/export.pdf`;
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) { toast.error('PDF export failed'); return; }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Dostination-seating-${eventId}-${stamp}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error('PDF export failed');
    }
  };

  const sendChat = async () => {
    const message = chatInput.trim();
    if (!message) return;
    setChatBusy(true);
    setChatMessages((prev) => [...prev, { role: 'user', content: message }]);
    setChatInput('');
    try {
      const { data } = await api.post(`/admin/events/${eventId}/seating/ai-chat`, {
        message, session_id: chatSessionId || undefined,
      });
      setChatSessionId(data.session_id);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.explanation || '', proposal: data.proposed_changes || [] },
      ]);
    } catch (e) {
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'The seating assistant is temporarily unavailable.', proposal: [] },
      ]);
    } finally { setChatBusy(false); }
  };

  const applyChatProposal = async (proposals) => {
    if (!Array.isArray(proposals) || proposals.length === 0) {
      toast.info('Rejected');
      return;
    }
    captureSnapshotForHistory();
    try {
      await api.post(`/admin/events/${eventId}/seating/ai-chat/apply`, {
        proposed_changes: proposals,
      });
      await fetchAll();
      toast.success(`Applied ${proposals.length} change(s).`);
    } catch (e) {
      toast.error('Apply failed');
    }
  };

  // Bundle 73 Task 2b - keyboard shortcuts. Wired via a page-level
  // keydown listener that skips when focus is inside a text input.
  useEffect(() => {
    const inEditableTarget = (t) => {
      if (!t) return false;
      const tag = (t.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return true;
      if (t.isContentEditable) return true;
      return false;
    };
    const onKeyDown = (e) => {
      if (inEditableTarget(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault(); undo(); return;
      }
      if (mod && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault(); redo(); return;
      }
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault(); setShowCheatsheet((v) => !v); return;
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setCanvasZoom((z) => Math.min(2.0, +(z + 0.1).toFixed(2))); return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        setCanvasZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2))); return;
      }
      if (e.key === '0') {
        e.preventDefault();
        setCanvasZoom(1); setCanvasPan({ x: 0, y: 0 }); return;
      }
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const step = 40;
        setCanvasPan((p) => ({
          x: p.x + (e.key === 'ArrowLeft' ? step : e.key === 'ArrowRight' ? -step : 0),
          y: p.y + (e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0),
        }));
        return;
      }
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault(); runAutoArrange(); return;
      }
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault(); downloadPdf(); return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // fetchAll / runAutoArrange / downloadPdf are referenced only.
  }, [undo, redo]);

  const counts = snapshot.counts || {};

  return (
    <div className="p-6 max-w-full">
      <Link to={`/admin/events/${eventId}/edit`} className="text-sm text-neutral-500 hover:text-primary-800 inline-flex items-center gap-1 mb-2">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-primary-900" data-testid="seating-page-title">
            Seating &amp; table arrangement
          </h1>
          <div className="text-xs text-neutral-500">
            {counts.seated || 0} seated, {counts.unseated || 0} unseated, {counts.tables || 0} tables
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={objective} onValueChange={setObjective}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="seating-objective-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="balance">Balance</SelectItem>
              <SelectItem value="affinity">Affinity</SelectItem>
              <SelectItem value="network_growth">Network growth</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={runAutoArrange} disabled={autoBusy} data-testid="seating-auto-arrange-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> {autoBusy ? 'Working...' : 'Auto-arrange'}
          </Button>
          <Button size="sm" variant="outline" onClick={addTable} data-testid="seating-add-table-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add table
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="seating-export-pdf-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Export PDF
          </Button>
          <Button size="sm" variant="outline" onClick={fetchAll} data-testid="seating-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={explainArrangement}
            disabled={narrativeBusy}
            data-testid="seating-explain-btn"
            className="border-secondary-200 text-secondary-800 hover:bg-secondary-50"
            title="Generate warm per-table narratives you can paste into welcome emails"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1" /> {narrativeBusy ? 'Thinking...' : 'Explain arrangement'}
          </Button>
          {/* Bundle 75 Task 1 - morning-of "meet your table" email dispatch. */}
          <Button
            size="sm" variant="outline"
            onClick={() => setSendModalOpen(true)}
            data-testid="seating-send-narratives-btn"
            className="border-secondary-200 text-secondary-800 hover:bg-secondary-50"
            title="Email each seated attendee a warm intro to their table"
          >
            <Send className="h-3.5 w-3.5 mr-1" /> Send narratives
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={openSentLog}
            data-testid="seating-sent-log-btn"
            title="See the last sends + Resend delivery statuses"
          >
            <Mail className="h-3.5 w-3.5 mr-1" /> Sent log
            {sentLog?.total ? <span className="ml-1 text-[10px] text-neutral-500">({sentLog.total})</span> : null}
          </Button>
        </div>
      </div>

      {/* Bundle 75 Task 1 - modal + sent-log dialog. */}
      <SendNarrativesModal
        open={sendModalOpen}
        onOpenChange={setSendModalOpen}
        eventId={eventId}
        onSent={() => { fetchSentLog(); }}
      />
      <Dialog open={sentLogOpen} onOpenChange={setSentLogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sent-log-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <Mail className="h-4 w-4 text-secondary-700" /> Narrative sent log
            </DialogTitle>
          </DialogHeader>
          {sentLogBusy ? (
            <div className="text-sm text-neutral-500 py-6 text-center">Loading...</div>
          ) : sentLog ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="sent-log-counts">
                {Object.entries(sentLog.counts || {}).map(([k, v]) => (
                  <span key={k} className="px-2 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200">
                    {k}: {v}
                  </span>
                ))}
              </div>
              <div className="border border-neutral-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-1.5 px-2">Sent at</th>
                      <th className="text-left py-1.5 px-2">To</th>
                      <th className="text-left py-1.5 px-2">Table</th>
                      <th className="text-left py-1.5 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sentLog.sends || []).map((s) => (
                      <tr key={s.id} className="border-t border-neutral-100" data-testid={`sent-log-row-${s.id}`}>
                        <td className="py-1.5 px-2 text-neutral-700">{s.sent_at ? new Date(s.sent_at).toLocaleString() : '-'}</td>
                        <td className="py-1.5 px-2 text-primary-900">{s.email}</td>
                        <td className="py-1.5 px-2 text-neutral-700">{s.table_name || '-'}</td>
                        <td className="py-1.5 px-2"><span className="text-[11px]">{s.delivery_status || 'sent'}</span></td>
                      </tr>
                    ))}
                    {!sentLog.sends?.length ? (
                      <tr><td colSpan={4} className="py-4 text-center text-neutral-500 text-xs">No sends yet.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Bundle 73 Task 1 - narrative panel */}
      {narrative && narrative.tables && narrative.tables.length > 0 ? (
        <Card className="mb-3" data-testid="seating-narrative-panel">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> At-your-table narratives
                {narrative.cached ? (
                  <span className="ml-1 opacity-60">(cached)</span>
                ) : null}
              </div>
              <Button
                size="sm" variant="outline" className="h-6 text-[10px]"
                onClick={copyAllNarratives}
                data-testid="seating-narrative-copy-all"
              >
                <Copy className="h-3 w-3 mr-1" /> Copy all
              </Button>
            </div>
            <div className="max-h-56 overflow-y-auto pr-1 space-y-2">
              {narrative.tables.map((row) => {
                const t = (snapshot.tables || []).find((x) => x.id === row.table_id);
                const heading = row.nickname
                  ? `${t?.name || 'Table'} - ${row.nickname}`
                  : (t?.name || 'Table');
                const clipboardBody = `${heading}\n${row.narrative || ''}`;
                return (
                  <div
                    key={row.table_id}
                    className="border border-neutral-100 rounded-md p-2 bg-white"
                    data-testid={`narrative-row-${row.table_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[11px] font-semibold text-primary-900">
                        {heading}
                      </div>
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-neutral-100"
                        onClick={() => copyText(clipboardBody)}
                        data-testid={`narrative-copy-${row.table_id}`}
                        title="Copy this table's narrative"
                      >
                        <Copy className="h-3 w-3 text-neutral-500" />
                      </button>
                    </div>
                    <div
                      className="text-[11px] text-primary-800 mt-1 leading-relaxed"
                      data-testid={`narrative-body-${row.table_id}`}
                    >
                      {row.narrative}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {proposal ? (
        <Card className="mb-3 border-secondary-300 bg-secondary-50/60" data-testid="seating-proposal-banner">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1">
              <div className="text-xs font-semibold text-primary-900">
                Proposal ready: score {proposal.score} | {proposal.diff?.length || 0} moves | {proposal.iterations} iters
              </div>
              {proposal.warnings?.length ? (
                <div className="text-[10px] text-amber-800">
                  {proposal.warnings.join(', ')}
                </div>
              ) : null}
            </div>
            <Button size="sm" variant="outline" onClick={() => setProposal(null)} data-testid="seating-proposal-reject">
              Reject
            </Button>
            <Button size="sm" onClick={applyProposal} data-testid="seating-proposal-apply">
              Apply proposal
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-[240px_1fr_320px] gap-3 h-[calc(100vh-220px)]">
        {/* Left: unseated pool */}
        <Card className="overflow-hidden">
          <CardContent className="p-3 flex flex-col h-full">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 inline-flex items-center gap-1">
              <Users className="h-3 w-3" /> Unseated ({(snapshot.unseated || []).length})
            </div>
            <div
              className="flex-1 overflow-y-auto space-y-1 pr-1"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const rid = e.dataTransfer.getData('text/registration_id');
                if (rid) unseatOne({ registration_id: rid });
              }}
              data-testid="seating-unseated-pool"
            >
              {(snapshot.unseated || []).map((a) => (
                <AttendeeCard key={a.registration_id} att={a} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Center: canvas */}
        <div
          className="overflow-hidden relative"
          data-testid="seating-canvas"
          ref={canvasRef}
        >
          <div
            className="overflow-auto h-full pb-2"
            style={{
              transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})`,
              transformOrigin: '0 0',
              transition: 'transform 150ms ease-out',
            }}
            data-testid="seating-canvas-transform"
          >
            {loading ? (
              <div className="p-10 text-center text-neutral-500">Loading...</div>
            ) : (snapshot.tables || []).length === 0 ? (
              <div className="p-10 text-center text-neutral-500 text-sm">
                No tables yet. Click Add table to get started.
              </div>
            ) : (
              <div className="flex gap-3 flex-wrap items-start">
                {(snapshot.tables || []).map((t) => (
                  <TableCard
                    key={t.id}
                    table={t}
                    members={membersByTable[t.id] || []}
                    onDropRegId={assignOne}
                    onEditTable={editTable}
                    onDeleteTable={deleteTable}
                    onToggleLock={toggleLock}
                    onUnseat={unseatOne}
                    warnings={[]}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Bundle 73 Task 2b - "?" help icon opens cheatsheet */}
          <button
            type="button"
            onClick={() => setShowCheatsheet(true)}
            className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-primary-900 text-white flex items-center justify-center shadow-md hover:bg-primary-800 transition-colors"
            data-testid="seating-cheatsheet-open"
            title="Keyboard shortcuts (?)"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2 left-2 text-[10px] text-neutral-500 bg-white/80 rounded px-2 py-0.5 backdrop-blur-sm">
            zoom {(canvasZoom * 100).toFixed(0)}%
          </div>
        </div>

        {/* Right: AI chat */}
        <Card className="overflow-hidden">
          <CardContent className="p-3 flex flex-col h-full">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 inline-flex items-center gap-1">
              <Bot className="h-3 w-3" /> Dost seating chat
            </div>
            <div
              className="flex-1 overflow-y-auto pr-1"
              data-testid="seating-chat-history"
            >
              {chatMessages.length === 0 ? (
                <div className="text-[11px] text-neutral-500 italic">
                  {'Ask Dost anything: "Balance table 3 for gender", "Why is X here", "Move Aisha to table 2".'}
                </div>
              ) : (
                chatMessages.map((m, i) => (
                  <AiChatMessage
                    key={i} msg={m}
                    onApplyProposal={applyChatProposal}
                  />
                ))
              )}
            </div>
            <div className="mt-2 flex gap-1.5">
              <Textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
                placeholder="Ask Dost..."
                className="text-xs min-h-[36px] resize-none"
                rows={1}
                data-testid="seating-chat-input"
              />
              <Button size="sm" onClick={sendChat} disabled={chatBusy || !chatInput.trim()} data-testid="seating-chat-send">
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bundle 73 Task 2b - keyboard shortcut cheatsheet modal */}
      <Dialog open={showCheatsheet} onOpenChange={setShowCheatsheet}>
        <DialogContent
          className="max-w-md"
          data-testid="seating-cheatsheet-modal"
        >
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <Keyboard className="h-4 w-4" /> Keyboard shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-[80px_1fr] gap-y-1.5 text-xs">
            {[
              ['+ / =', 'Zoom in'],
              ['- / _', 'Zoom out'],
              ['0', 'Reset zoom + pan'],
              ['Arrow keys', 'Pan canvas'],
              ['Ctrl/Cmd Z', 'Undo last change'],
              ['Ctrl/Cmd Shift Z', 'Redo'],
              ['A', 'Auto-arrange (opens proposal)'],
              ['E', 'Export PDF'],
              ['?', 'Toggle this cheatsheet'],
            ].map(([k, v]) => (
              <React.Fragment key={k}>
                <kbd
                  className="px-1.5 py-0.5 rounded border border-neutral-300 bg-neutral-100 text-[10px] font-mono text-primary-900 self-start"
                >{k}</kbd>
                <div className="text-neutral-700">{v}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-neutral-500 italic">
            Shortcuts are disabled while typing in a text field.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSeatingPage;
